<script setup lang="ts">
import 'vue-tel-input/vue-tel-input.css'
import { useVModel } from '@vueuse/core'
import { ref, watch } from 'vue'
import { VueTelInput } from 'vue-tel-input'
import { useColorMode } from '@/composables/src/useColorMode'
// Remove incorrect emit import

type Phone = {
  country: string
  countryCallingCode?: string
  countryCode: string
  formatted: string | undefined
  nationalNumber: string
  number?: string
  possible: boolean
  valid: boolean
}

const props = defineProps<{
  modelValue: Phone | undefined
  vmodelAsString?: string | undefined
  defaultValue?: Phone | undefined
  fieldName: string
  class?: string
  autoDefaultCountry?: boolean
  defaultCountry?: string
}>()
const phone = ref()
const { mode } = useColorMode()

watch(
  () => props.vmodelAsString,
  (newValue) => {
    if (typeof newValue === 'string') {
      phone.value = newValue
    }
  },
  { immediate: true },
)
const immediateErrorMessage = ref<string>('')
const emits = defineEmits<(e: 'update:modelValue', payload: string | number) => void>()
const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

const inputOptions = {
  placeholder: '+1 800 555 0100',
}

const validatePhoneNumber = (phoneObject: Phone) => {
  if (phoneObject && phoneObject.valid) {
    immediateErrorMessage.value = ''
  } else {
    immediateErrorMessage.value = 'Please enter a valid phone number.'
  }
}

const changedPhoneNumber = (_: string, phoneObject: Phone) => {
  modelValue.value = phoneObject.number || phoneObject.formatted ? phoneObject : undefined
}
</script>

<template>
  <VueTelInput
    v-model="phone"
    class="mt-2 h-10 !rounded-xl !border-input text-sm focus-within:!shadow-none"
    :class="[props.class, { 'bg-white': mode === 'light', 'bg-sidebar-accent': mode === 'dark' }]"
    mode="international"
    :input-options="inputOptions"
    :auto-default-country="props?.autoDefaultCountry || false"
    :default-country="props?.defaultCountry || 'US'"
    :valid-characters-only="true"
    @validate="validatePhoneNumber"
    @on-input="changedPhoneNumber"
  ></VueTelInput>
</template>

<style lang="postcss" scoped>
:deep(.vti__dropdown) {
  @apply rounded-xl;
  &:hover {
    .light & {
      @apply !bg-accent;
    }
    .dark & {
      @apply !bg-[#343840];
    }
  }
}
:deep(.vti__dropdown.open),
:deep(.vti__dropdown-list) {
  @apply font-normal border-border;
  strong {
    @apply font-normal;
  }
  .vti__dropdown-item.highlighted {
    .light & {
      @apply !bg-accent;
    }
    .dark & {
      @apply !bg-[#343840];
    }
  }
  .light & {
    @apply !bg-white;
  }
  .dark & {
    @apply !bg-sidebar-accent;
  }
}
</style>
